import {type RefObject, useEffect} from "react";

import {isServer} from "@/shared/utils/runtime";

interface UseElementCssHeightProps {
    ref: RefObject<HTMLElement | null>;
    cssVariable: string;
}

/**
 * Tracks an element's height via ResizeObserver and sets a CSS custom property on :root.
 * Useful for exposing the header height (or any element) to the rest of the page via CSS.
 *
 * @example
 * useElementCssHeight({ ref: headerRef, cssVariable: "--header-height" });
 * // Then in CSS: top: var(--header-height);
 */
export function useElementCssHeight({ref, cssVariable}: UseElementCssHeightProps): void {
    useEffect(() => {
        if (isServer()) return;

        const element = ref.current;
        if (!element) return;

        const updateHeight = (): void => {
            const height = element.getBoundingClientRect().height;
            document.documentElement.style.setProperty(cssVariable, `${height}px`);
        };

        updateHeight();

        const observer = new ResizeObserver(updateHeight);
        observer.observe(element);

        return () => {
            observer.disconnect();
            document.documentElement.style.removeProperty(cssVariable);
        };
    }, [ref, cssVariable]);
}
